import { z } from 'zod';

// ---------------------------------------------------------------------------
// Discord inspection payload schemas.
//
// These mirror the shapes the bot's message-inspector writes into the
// inspection log (apps/bot/.inspection-log.jsonl). They're the contract
// between the inspector (writer) and the normaliser (reader). Keeping them
// in packages/types means the bot and any future consumer (a replay tool,
// a Deno edge function) parse the same payload from the same definition.
// ---------------------------------------------------------------------------

export const DiscordInspectionAttachmentSchema = z.object({
  id: z.string(),
  contentType: z.string().nullable(),
  size: z.number().int().nonnegative(),
  url: z.string(),
  name: z.string().nullable(),
});
export type DiscordInspectionAttachment = z.infer<typeof DiscordInspectionAttachmentSchema>;

export const DiscordInspectionStickerSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type DiscordInspectionSticker = z.infer<typeof DiscordInspectionStickerSchema>;

// Discord embeds carry a deep, type-varying shape (videos, GIFs, articles,
// poll results...). The normaliser only inspects a few top-level fields so
// we keep the schema permissive and treat the rest as opaque jsonb, archived
// in raw_discord_event for any future tooling.
export const DiscordInspectionEmbedSchema = z
  .object({
    type: z.string().optional(),
    url: z.string().optional(),
    provider: z
      .object({ name: z.string().optional(), url: z.string().optional() })
      .partial()
      .optional(),
  })
  .passthrough();
export type DiscordInspectionEmbed = z.infer<typeof DiscordInspectionEmbedSchema>;

export const DiscordInspectionReferenceSchema = z.object({
  channelId: z.string().optional(),
  guildId: z.string().optional(),
  messageId: z.string().optional(),
  type: z.number().int(),
});
export type DiscordInspectionReference = z.infer<typeof DiscordInspectionReferenceSchema>;

export const DiscordInspectionPollAnswerSchema = z.object({
  id: z.number().int(),
  text: z.string().nullable(),
  emoji: z
    .object({
      id: z.string().nullable(),
      name: z.string().nullable(),
      animated: z.boolean().nullable(),
    })
    .nullable(),
  voteCount: z.number().int().nonnegative(),
});
export type DiscordInspectionPollAnswer = z.infer<typeof DiscordInspectionPollAnswerSchema>;

export const DiscordInspectionPollSchema = z.object({
  question: z.object({ text: z.string() }),
  allowMultiselect: z.boolean(),
  layoutType: z.number().int(),
  expiresTimestamp: z.number().nullable(),
  resultsFinalized: z.boolean(),
  answers: z.array(DiscordInspectionPollAnswerSchema),
});
export type DiscordInspectionPoll = z.infer<typeof DiscordInspectionPollSchema>;

// Output of buildInspectionPayload in apps/bot/src/discord/message-inspector.ts.
// The normaliser parses against this; the inspector emits this shape. Any
// drift between the two is caught at parse time during ingestion.
export const DiscordMessageInspectionSchema = z.object({
  connectionId: z.string().uuid(),
  guildId: z.string(),
  channelId: z.string(),
  channelName: z.string().nullable(),
  isThread: z.boolean(),
  parentChannelId: z.string().nullable(),
  parentChannelName: z.string().nullable(),
  messageId: z.string(),
  authorId: z.string(),
  authorTag: z.string(),
  authorBot: z.boolean(),
  contentLength: z.number().int().nonnegative(),
  content: z.string(),
  attachments: z.array(DiscordInspectionAttachmentSchema),
  embeds: z.array(DiscordInspectionEmbedSchema),
  stickers: z.array(DiscordInspectionStickerSchema),
  reference: DiscordInspectionReferenceSchema.nullable(),
  poll: DiscordInspectionPollSchema.nullable(),
  type: z.number().int(),
  createdTimestamp: z.number(),
  editedTimestamp: z.number().nullable(),
});
export type DiscordMessageInspection = z.infer<typeof DiscordMessageInspectionSchema>;

// ---------------------------------------------------------------------------
// Discord message types we explicitly handle. The numeric values match the
// Discord gateway. Anything not listed here flows through with the default
// branch and is preserved in raw_discord_event for replay.
//
// References:
//   https://discord.com/developers/docs/resources/message#message-object-message-types
// ---------------------------------------------------------------------------
export const DISCORD_MESSAGE_TYPE = {
  DEFAULT: 0,
  CHANNEL_PINNED_MESSAGE: 6,
  // Discord auto-creates this as a duplicate of the first message in a
  // forum thread. Skipping it avoids two feedback rows for the same content.
  THREAD_STARTER_MESSAGE: 18,
  REPLY: 19,
  THREAD_CREATED: 21,
  // Auto-posted by Discord when a poll closes. Folded into the parent
  // poll record rather than ingested as a feedback message.
  POLL_RESULT: 46,
} as const;

// ---------------------------------------------------------------------------
// Reaction inspection payload. Discord delivers MessageReactionAdd /
// MessageReactionRemove events; the bot filters by watched channel and
// emits this shape for the persister + JSONL log.
//
// `identifier` distinguishes unicode (id=null, name='👍') from custom
// guild emoji (id present, name = code, animated bool). The full union
// is preserved so the analytics layer can decide whether to map a 👍 to
// a vote, a :crash: custom emoji to a tag, etc.
// ---------------------------------------------------------------------------
export const DiscordInspectionEmojiSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  animated: z.boolean().nullable(),
  identifier: z.string(),
});
export type DiscordInspectionEmoji = z.infer<typeof DiscordInspectionEmojiSchema>;

export const DiscordReactionInspectionSchema = z.object({
  action: z.enum(['add', 'remove']),
  connectionId: z.string().uuid(),
  guildId: z.string(),
  channelId: z.string(),
  isThread: z.boolean(),
  parentChannelId: z.string().nullable(),
  messageId: z.string(),
  messageAuthorId: z.string(),
  reactorId: z.string(),
  emoji: DiscordInspectionEmojiSchema,
  count: z.number().int().nonnegative(),
});
export type DiscordReactionInspection = z.infer<typeof DiscordReactionInspectionSchema>;

// ---------------------------------------------------------------------------
// Poll vote inspection payload. Discord delivers MessagePollVoteAdd /
// MessagePollVoteRemove with the answer + the voting user; the bot
// resolves the parent message + watched-channel filter and emits this
// shape. The persister writes one feedback_poll_vote row per event.
// ---------------------------------------------------------------------------
export const DiscordPollVoteInspectionSchema = z.object({
  action: z.enum(['add', 'remove']),
  connectionId: z.string().uuid(),
  guildId: z.string(),
  channelId: z.string(),
  isThread: z.boolean(),
  parentChannelId: z.string().nullable(),
  messageId: z.string(),
  messageAuthorId: z.string(),
  voterId: z.string(),
  answer: DiscordInspectionPollAnswerSchema,
  pollQuestion: z.string().nullable(),
});
export type DiscordPollVoteInspection = z.infer<typeof DiscordPollVoteInspectionSchema>;
